// src/services/authService.ts
// Authentication service with SSO support

import axios from 'axios';
import * as jose from 'jose';
import { getCookie, setCookie, clearAuthCookies } from '../utils/cookieUtils';
import { Role, isRole } from '../constants/roles';
import { clearFacilityFromStorage } from '../utils/facilityStorage';
import api from './axiosConfig';
import { LogoutReason, broadcastLogout, reportSessionEnd } from './sessionEvents';

const WRITE_API_URL = process.env.REACT_APP_FLUX_API_URL;

// ============================================
// Types
// ============================================

export interface User {
  id: number | string;
  email: string;
  name?: string;
  roles: string[];
  canAccessUserManagement: boolean;
  pages: string[];
  account_id?: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  email: string;
  id: number | string;
  roles: string[];
  pages: string[];
  username?: string;
}

export interface CheckAuthMethodResponse {
  auth_type: 'sso' | 'password';
  provider?: string;
  redirect_url?: string;
}

export interface RefreshTokenResponse {
  token: string;
  status: 'success';
}

// ============================================
// Token Management
// ============================================

/**
 * Set the auth token in cookie and axios headers
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    const { exp } = jose.decodeJwt(token);
    const expiry = new Date((exp as number) * 1000).toUTCString();
    setCookie('authToken', token, { expires: expiry });
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

/**
 * Set the auth header only (when cookie is already set by backend)
 */
export function setAuthHeader(token: string | null): void {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
}

/**
 * Get the current auth token from cookie
 */
export function getAuthToken(): string | null {
  return getCookie('authToken');
}

/**
 * Check if current token is expired or about to expire
 */
export function isTokenExpired(token: string, bufferSeconds: number = 120): boolean {
  try {
    const { exp } = jose.decodeJwt(token);
    if (!exp) return true;
    const expiryTime = (exp as number) * 1000;
    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;
    return expiryTime - now < bufferMs;
  } catch {
    return true;
  }
}

/**
 * Decode JWT and extract claims
 */
export function decodeToken(token: string): jose.JWTPayload | null {
  try {
    return jose.decodeJwt(token);
  } catch {
    return null;
  }
}

// ============================================
// Auth Method Detection (SSO)
// ============================================

/**
 * Check authentication method for an email address.
 * Returns whether user should use SSO or password login.
 */
export async function checkAuthMethod(email: string): Promise<CheckAuthMethodResponse> {
  const response = await axios.post<CheckAuthMethodResponse>(
    `${WRITE_API_URL}/admin/check_auth_method`,
    { email: email.toLowerCase().trim() }
  );
  return response.data;
}

// ============================================
// Login Functions
// ============================================

/**
 * Login with email and password (existing flow)
 */
export async function login(credentials: LoginCredentials): Promise<User> {
  const response = await axios.post<LoginResponse>(
    `${WRITE_API_URL}/admin/login`,
    credentials,
    { withCredentials: true }
  );

  const { token, email, id, roles, pages, username } = response.data;

  const user: User = {
    id,
    name: username || '',
    roles: roles || [Role.admin],
    email,
    pages: pages || [],
    canAccessUserManagement: false
  };

  setAuthToken(token);

  // Only check user management access for non-carrier users
  const isCarrier = roles && roles.some((role) => isRole(role, Role.carrier));
  if (!isCarrier) {
    try {
      const userManagementResponse = await axios.get(
        `${WRITE_API_URL}/admin/can_access_user_management`
      );
      user.canAccessUserManagement = userManagementResponse.data.result;
    } catch (error) {
      console.error('Could not check user management access:', error);
      user.canAccessUserManagement = false;
    }
  }

  localStorage.setItem('user', JSON.stringify(user));

  return user;
}

/**
 * Initiate SSO login by redirecting to IdP
 */
export function initiateSSO(redirectUrl: string): void {
  window.location.href = redirectUrl;
}

// ============================================
// Token Refresh (SSO)
// ============================================

/** Refresh once the access token is this close to expiring. */
const REFRESH_BUFFER_SECONDS = 3600;

/** Browser-wide lock name: only the tab holding it may call /admin/refresh_token. */
const REFRESH_LOCK_NAME = 'freightroll_token_refresh';

/**
 * Why a refresh failed. Only `rejected` means the session is over: the server
 * answered and refused the refresh token (or none was sent). `unavailable`
 * means the server couldn't be reached or errored, and the session may still
 * be fine, so callers must not log the user out for it.
 */
export class RefreshError extends Error {
  constructor(
    public kind: 'rejected' | 'unavailable',
    public status?: number
  ) {
    super(`token refresh ${kind}${status ? ` (${status})` : ''}`);
    this.name = 'RefreshError';
  }
}

export function isRefreshRejected(error: unknown): boolean {
  return error instanceof RefreshError && error.kind === 'rejected';
}

function toRefreshError(error: unknown): RefreshError {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  // 400: no refresh cookie was sent. 401: it was invalid, expired or revoked.
  // 403: the user is no longer allowed in.
  if (status === 400 || status === 401 || status === 403) {
    return new RefreshError('rejected', status);
  }
  return new RefreshError('unavailable', status);
}

/** Same-tab dedup: concurrent callers in one tab share one refresh. */
let _refreshPromise: Promise<string> | null = null;

/**
 * After the server rejects a refresh, proactive refreshes (shouldRefreshToken)
 * pause for this long instead of retrying on every request and timer tick.
 * A 401 still triggers a refresh attempt.
 */
const PROACTIVE_REFRESH_BACKOFF_MS = 5 * 60 * 1000;
let _lastRefreshRejectedAt = 0;

/**
 * Run `fn` while holding the browser-wide refresh lock, so tabs refresh one
 * at a time. Browsers without the Web Locks API fall back to the same-tab
 * dedup only.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request(REFRESH_LOCK_NAME, fn) as Promise<T>;
  }
  return fn();
}

/**
 * Refresh the access token using the httpOnly refresh token cookie.
 *
 * Refresh tokens are single use, so two tabs refreshing at once would have one
 * of them fail and the server clear the cookies for the whole browser. Holding
 * the lock prevents that. A tab that waited for the lock checks whether the
 * cookie (shared by all tabs) already holds a newer token and reuses it
 * instead of refreshing again.
 *
 * Throws a RefreshError; see its doc for which failures end the session.
 */
export async function refreshAccessToken(): Promise<string> {
  if (_refreshPromise) {
    return _refreshPromise;
  }

  const tokenBefore = getAuthToken();

  _refreshPromise = withRefreshLock(async () => {
    const current = getAuthToken();
    if (
      current &&
      current !== tokenBefore &&
      !isTokenExpired(current, REFRESH_BUFFER_SECONDS)
    ) {
      setAuthHeader(current);
      return current;
    }

    try {
      const response = await axios.post<RefreshTokenResponse>(
        `${WRITE_API_URL}/admin/refresh_token`,
        {},
        { withCredentials: true }
      );

      const newToken = response.data.token;
      // Update both the cookie and axios header so all request paths
      // (axios interceptor + graphqlFetch) use the new token immediately
      setAuthToken(newToken);
      _lastRefreshRejectedAt = 0;
      return newToken;
    } catch (err) {
      const refreshError = toRefreshError(err);
      if (refreshError.kind === 'rejected') _lastRefreshRejectedAt = Date.now();
      throw refreshError;
    }
  });

  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

// ============================================
// Logout
// ============================================

/**
 * Logout from all sessions.
 * Calls backend to invalidate refresh tokens, then clears client state.
 * Returns the IdP SLO redirect URL if the user was logged in via SSO,
 * so the caller can redirect the browser to terminate the IdP session.
 */
let _logoutPromise: Promise<string | null> | null = null;

export async function logoutWithApi(): Promise<string | null> {
  if (_logoutPromise) return _logoutPromise;

  _logoutPromise = (async () => {
    const token = getAuthToken();
    let sloRedirectUrl: string | null = null;

    reportSessionEnd('user_logout');

    // Call backend to invalidate all refresh tokens
    if (token) {
      try {
        const response = await api.post('/admin/logout');
        sloRedirectUrl = response.data?.slo_redirect_url || null;
      } catch (error) {
        // Log but don't block logout on API failure
        console.error('Logout API call failed:', error);
      }
    }

    // Clear client-side state, then sign out this browser's other tabs.
    clearAuthState();
    broadcastLogout('user_logout');

    return sloRedirectUrl;
  })();

  try {
    return await _logoutPromise;
  } finally {
    _logoutPromise = null;
  }
}

/**
 * Clear all client-side auth state (used by logout and on auth failure)
 * Synchronous version for immediate cleanup
 */
export function clearAuthState(): void {
  // Clear cookies
  clearAuthCookies();

  // Clear localStorage
  localStorage.removeItem('user');
  clearFacilityFromStorage();
  localStorage.removeItem('yardManagement_zoneFilter');
  localStorage.removeItem('yardManagement_spotBatch');
  localStorage.removeItem('yardManagement_showUnallocated');

  // Clear axios header
  setAuthHeader(null);
}

/**
 * End this tab's session because the server refused it, recording why.
 * Unlike logoutWithApi this leaves the server session and the other tabs
 * alone: a refusal seen by one tab is not proof the others are signed out.
 */
export function endLocalSession(reason: LogoutReason, httpStatus?: number): void {
  // Report first: it reads the access token that clearAuthState removes.
  reportSessionEnd(reason, httpStatus);
  clearAuthState();
}

let _redirectingToLogin = false;

/**
 * End this tab's session and load the login page. The first caller wins, so a
 * burst of refused requests produces one report and one redirect.
 */
export function redirectToLogin(reason: LogoutReason, httpStatus?: number): void {
  if (_redirectingToLogin) return;
  _redirectingToLogin = true;
  endLocalSession(reason, httpStatus);
  window.location.href = '/login';
}

export function isRedirectingToLogin(): boolean {
  return _redirectingToLogin;
}

// ============================================
// Session Validation
// ============================================

/**
 * Check if user has a valid session (token exists and not expired)
 */
export function hasValidSession(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  return !isTokenExpired(token, 0); // No buffer, just check if expired
}

/**
 * Check if token should be refreshed (exists but expiring soon)
 */
export function shouldRefreshToken(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  if (Date.now() - _lastRefreshRejectedAt < PROACTIVE_REFRESH_BACKOFF_MS) return false;
  return isTokenExpired(token, REFRESH_BUFFER_SECONDS);
}

// ============================================
// User Management Access (existing)
// ============================================

export async function checkUserManagementAccess(email: string): Promise<boolean> {
  try {
    const response = await axios.post(
      `${WRITE_API_URL}/admin/check_user_management_access`,
      { email }
    );
    return response.data?.can_access === true;
  } catch {
    return false;
  }
}

// ============================================
// Validate Token (placeholder - existing)
// ============================================

export async function validateToken(token: string): Promise<boolean> {
  // Placeholder for token validation
  // Currently relies on API calls to validate
  return true;
}

// ============================================
// Current User (SSO)
// ============================================

// Re-export from hook for convenience
export { fetchCurrentUser } from '../hooks/useCurrentUser';
export type {
  CurrentUser,
  Permissions,
  Facility as UserFacility
} from '../hooks/useCurrentUser';
